import {
  ProgramWizardMessage,
  ProgramWizardMetadata,
  ProgramWizardConfig,
} from './types';
import { getProgramWizardConfigForProductAndPackage } from './utils';
import { ProgramFetcher } from './ProgramFetcher';
import {
  ProgramWizardContextProvider,
  useProgramWizardContext,
} from './program-wizard-context';
import {
  defaultProgramDependentContextValues,
  ProgramDependentDataContextState,
  ProgramDependentDataProvider,
  ProgramSubmitState,
  useProgramDependentDataContext,
} from './program-dependent-data-context';
import {
  StepWizard,
  DataStore,
  StepWizardRenderProps,
  DataStoreState,
  StepInfo,
  useStepWizardContext,
  useDataStore,
} from '../step-wizard';
import { OmitStrict } from '../../types';
import { OktaUserInfo } from '../../auth';
import browserHistory from '@/app/core/history';
import { modalUtils, packKeys, unpackKeys } from '@/app/core/utils';
import { handleSave } from '@/app/features/products/common/handler/ProgramButtonsHandler';
import { useSetPageTitle } from '@/app/store';
import {
  cloneElement,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaBackward,
  FaSave,
} from 'react-icons/fa/index.js';
import {
  AnyRecord,
  CancelOperationError,
  PackageNameEnum,
  ProductNameEnum,
  ProgramDataType,
} from '@ssar-app/domain';
import { useLocation, Prompt, useHistory } from 'react-router-dom';
import { parse as qsParse } from 'qs';
import classnames from 'classnames';
import {
  Alert,
  Spin,
  Steps,
  StepProps,
  StepStatus,
  SummaryCard,
} from '@ssar-app/uikit';
import { Box, Button, Divider, Flex, HStack, Text } from '@chakra-ui/react';
import { usePathContext } from '@ssar-app/okta-authn-react';

const buildKeysFn = packKeys('.');
const extractKeysFn = unpackKeys('.');

export type ProgramWizardProps = {
  productName: ProductNameEnum;
  packageName: PackageNameEnum;
  metadata: ProgramWizardMetadata;
  header?: ReactNode;
  footer?: ReactNode;
  children?: (
    stepWizard: StepWizardRenderProps,
    dataStoreProps: DataStoreState,
  ) => ReactNode;
  warnings?: (messages: ProgramWizardMessage[]) => ReactNode;
  validations?: {
    preSubmit?: (
      data: ProgramDataType,
      metaData?: AnyRecord,
      setMetaData?: (metaData: AnyRecord) => void,
    ) => Promise<ProgramDataType>;
    postSubmit?: (
      data: ProgramDataType,
      metaData?: AnyRecord,
      setMetaData?: (metaData: AnyRecord) => void,
    ) => Promise<ProgramDataType>;
  };
  handlers?: {
    summaryHandler?: (
      model: ProgramDataType,
      programId: string,
      version: string,
      showAccept: boolean,
      buildPath: (...paths: string[]) => string,
      draftId?: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      history?: any,
      setIsProcessing?: (processing: boolean) => void,
    ) => Promise<void>;
    programNameExtractor?: (model: ProgramDataType) => string;
  };
};

enum ProgramWizardLoadStateEnum {
  'Unknown',
  'LoadingProgram',
  'LoadedProgram',
}

const ProgramWizardWithConfig = memo(
  (props: {
    config: ProgramWizardConfig;
    metadata: ProgramWizardMetadata;
    children?: ReactNode;
  }) => {
    const { config, children, metadata } = props;

    return (
      <ProgramWizardContextProvider
        value={{
          config,
          metadata,
        }}
      >
        {children}
      </ProgramWizardContextProvider>
    );
  },
);

export const ProgramWizard = (props: ProgramWizardProps) => {
  const {
    productName,
    packageName,
    metadata,
    children,
    header,
    footer,
    warnings,
    validations,
    handlers,
  } = props;

  const [loadStateAndProgram, setLoadStateAndProgram] = useState<{
    loadState: ProgramWizardLoadStateEnum;
    program: Partial<ProgramDataType> | null;
    messages: ProgramWizardMessage[];
    programDependentData: ProgramDependentDataContextState;
  }>({
    loadState: ProgramWizardLoadStateEnum.Unknown,
    program: null,
    messages: [],
    programDependentData: defaultProgramDependentContextValues,
  });
  const [programWizardConfig, setProgramWizardConfig] =
    useState<ProgramWizardConfig | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const userProfileRef = useRef(OktaUserInfo.getOktaUserInfo());
  const [updateWarnings, setUpdateWarnings] = useState<boolean>(false);
  const [forceSubmitState, setForceSubmitState] =
    useState<ProgramSubmitState>('Init');
  const [programMetaData, setProgramMetaData] = useState<AnyRecord>({});

  const toggleUpdateWarning = () => {
    setUpdateWarnings((updateWarnings) => !updateWarnings);
  };

  const toggleForceSubmitState = () => {
    setForceSubmitState(
      forceSubmitState === 'Force' ? 'ForceContinue' : 'Force',
    );
    scrollToTopOfTheWizard();
  };

  const wizardTopRef = useRef<HTMLDivElement>(null);
  const scrollToTopOfTheWizard = useCallback(
    () => wizardTopRef.current?.scrollIntoView(),
    [],
  );

  useEffect(() => {
    const loadProgramConfig = async () => {
      try {
        const programConfig = await getProgramWizardConfigForProductAndPackage(
          productName,
          packageName,
        );
        setProgramWizardConfig(programConfig);
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          setError(err);
        } else {
          setError(new Error(err?.toString() || 'Unknown error'));
        }
      }
    };
    loadProgramConfig();
  }, [packageName, productName]);
  useEffect(() => {
    if (loadStateAndProgram.loadState === ProgramWizardLoadStateEnum.Unknown) {
      setLoadStateAndProgram((state) => {
        return {
          ...state,
          loadState: ProgramWizardLoadStateEnum.LoadingProgram,
        };
      });
    }
  }, [loadStateAndProgram.loadState]);

  const steps: StepInfo[] = useMemo(() => {
    if (programWizardConfig?.steps) {
      return programWizardConfig.steps.map((step) => {
        const { key, ...rest } = step;
        const toReturn: StepInfo = {
          key: buildKeysFn(key),
          step: rest,
        };
        return toReturn;
      });
    }
    return [];
  }, [programWizardConfig?.steps]);

  if (error) {
    return (
      <Alert
        description={error.message || error.toString()}
        title='Error'
        type='error'
        showIcon
        isOpen
        showCloseButton={false}
      />
    );
  } else if (
    loadStateAndProgram.loadState === ProgramWizardLoadStateEnum.LoadingProgram
  ) {
    return (
      <>
        <Text as='h1'>Loading Program</Text>
        {programWizardConfig && (
          <ProgramWizardWithConfig
            config={programWizardConfig}
            metadata={metadata}
          >
            <ProgramFetcher
              productName={productName}
              packageName={packageName}
              transformer={programWizardConfig.preTransformer}
              programWizardMetadata={metadata}
              userProfile={userProfileRef.current}
              onCancel={(messages, e) => {
                setError(new CancelOperationError(e));
              }}
              onError={(messages, e) => {
                setError(e);
              }}
              onSuccess={(messages, program, programDependentData) => {
                setLoadStateAndProgram((_state) => {
                  return {
                    loadState: ProgramWizardLoadStateEnum.LoadedProgram,
                    program,
                    messages: messages as ProgramWizardMessage[],
                    programDependentData: programDependentData,
                  };
                });
              }}
            ></ProgramFetcher>
          </ProgramWizardWithConfig>
        )}
      </>
    );
  } else if (
    loadStateAndProgram.loadState ===
      ProgramWizardLoadStateEnum.LoadedProgram &&
    programWizardConfig
  ) {
    return (
      <Box bg='white' w={'full'} shadow='base' borderRadius={'md'}>
        <ProgramWizardWithConfig
          config={programWizardConfig}
          metadata={metadata}
        >
          <ProgramDependentDataProvider
            value={{
              data: loadStateAndProgram.programDependentData,
              metaData: programMetaData,
              setMetaData: setProgramMetaData,
              updateWarnings: updateWarnings,
              setUpdateWarnings: toggleUpdateWarning,
              forceSubmitState: forceSubmitState,
              setForceSubmitState: toggleForceSubmitState,
            }}
          >
            <StepWizard steps={steps}>
              {(stepWizardProps) => {
                return (
                  <DataStore
                    initialData={loadStateAndProgram.program || undefined}
                    buildKey={buildKeysFn}
                    extractKeys={extractKeysFn}
                  >
                    {(dataStoreState) => {
                      return (
                        <Spin
                          spinning={stepWizardProps.wizardState.areStepsLoading}
                          blurContainerProps={{ p: 4 }}
                        >
                          {warnings
                            ? warnings(loadStateAndProgram.messages)
                            : null}
                          <div ref={wizardTopRef}></div>
                          {header}
                          {children
                            ? children(stepWizardProps, dataStoreState)
                            : null}
                          {cloneElement(footer as React.ReactElement, {
                            validations: validations,
                            handlers: handlers,
                          })}
                        </Spin>
                      );
                    }}
                  </DataStore>
                );
              }}
            </StepWizard>
          </ProgramDependentDataProvider>
        </ProgramWizardWithConfig>
      </Box>
    );
  }
  return null;
};

export type ProgramWizardHeaderProps = React.HTMLAttributes<HTMLDivElement>;

export const ProgramWizardHeader = (props: ProgramWizardHeaderProps) => {
  const {
    goToStep,
    activeStep,
    wasStepActive: wasStepActiveFn,
  } = useStepWizardContext();
  const { ...rest } = props;
  const {
    buildKey,
    extractKeys: _extractKeys,
    getDataPartState,
  } = useDataStore();

  const {
    config: { steps },
  } = useProgramWizardContext();

  const { forceSubmitState } = useProgramDependentDataContext();

  const prepareStepStatuses = (
    isForceSubmit: boolean,
  ): Record<string, StepStatus> => {
    const toReturn: Record<number, StepStatus> = {};
    steps.forEach((step, index) => {
      const stepKey = buildKey(step.key);
      const { isValid: isStepValid } = getDataPartState(step.key) || {};
      const wasStepActive = isForceSubmit
        ? isForceSubmit
        : wasStepActiveFn(stepKey);
      const isStepActive = activeStep === stepKey;
      let stepStatus: StepProps['status'] = 'wait';
      if (isStepActive) {
        stepStatus = 'process';
      } else {
        if (isStepValid === undefined || isStepValid === false) {
          if (wasStepActive) {
            stepStatus = 'error';
          } else {
            stepStatus = 'wait';
          }
        } else {
          if (wasStepActive) {
            stepStatus = 'finish';
          } else {
            stepStatus = 'wait';
          }
        }
      }
      toReturn[index] = stepStatus;
    });
    return toReturn;
  };

  const onStepClick = useCallback(
    (stepKey: string) => {
      return () => {
        if (activeStep !== stepKey) {
          goToStep(stepKey);
        }
      };
    },

    [activeStep, goToStep],
  );

  return (
    <Flex {...rest}>
      <Steps
        isVertical={false}
        colorScheme='blue'
        current={steps.findIndex((a) => buildKey(a.key) === activeStep)}
      >
        {steps.map((step, index) => {
          const stepKey = buildKey(step.key);
          const stepStatuses = prepareStepStatuses(forceSubmitState !== 'Init');
          return (
            <Steps.Step
              status={stepStatuses[index]}
              onStepClick={onStepClick(stepKey)}
              title={step.title}
              key={stepKey}
              description={step.description}
              w={'fit-content'}
              showBottomBorder={true}
            />
          );
        })}
      </Steps>
    </Flex>
  );
};

export type ProgramWizardFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  validations?: ProgramWizardProps['validations'];
  handlers?: ProgramWizardProps['handlers'];
};
export const ProgramWizardFooter = (props: ProgramWizardFooterProps) => {
  const { areStepsLoading, isFirstStep, isLastStep, nextStep, previousStep } =
    useStepWizardContext();
  const { config, metadata } = useProgramWizardContext();
  const { steps } = config;
  const { isValidAsDraft, data, getDataPartState } = useDataStore();
  const isSaveDraftsAllowed = config.isSaveDraftsAllowed;
  const { className, validations, handlers, ...rest } = props;

  const history = useHistory();
  const { buildPath } = usePathContext();
  const { metaData, setForceSubmitState, forceSubmitState, setMetaData } =
    useProgramDependentDataContext();
  const [draftId, setDraftId] = useState(metadata.draftId || '');
  const [programId, setProgramId] = useState(metadata.from || '');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loading = areStepsLoading;
  const autoSaveRef = useRef<HTMLButtonElement>(null);
  const autoSaveDraft = (): void => {
    if (config.isAutoSaveAllowed) {
      setTimeout(() => {
        autoSaveRef.current?.click();
      }, config.autoSaveTimer);
    }
  };

  useEffect(() => {
    autoSaveDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const saveClickHandle = (exitProgram: boolean, isAutoSave: boolean) => {
    if (isAutoSave && (isProcessing || isSubmitting)) {
      autoSaveDraft();
      return;
    }
    setIsProcessing(true);
    handleSave(
      handlers?.programNameExtractor?.(data as ProgramDataType) || '',
      draftId,
      programId,
      data as ProgramDataType,
      exitProgram,
      isAutoSave,
    )
      .then(({ newDraftId, newProgramId }) => {
        setDraftId(newDraftId);
        setProgramId(newProgramId);
        if (exitProgram) {
          history.push(buildPath(`/dashboard?show=drafts`), 'EXIT');
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsProcessing(false);
        isAutoSave && autoSaveDraft();
      });
  };

  const submitClickHandle = async () => {
    const invalidSteps = steps.filter((step) => {
      const { isValid: isStepValid } = getDataPartState(step.key) || {};
      return isStepValid === undefined || isStepValid === false;
    });
    if (invalidSteps.length === 0) {
      setIsSubmitting(true);
      try {
        await validations?.preSubmit?.(
          data as ProgramDataType,
          metaData,
          setMetaData,
        );
        await handlers?.summaryHandler?.(
          data as ProgramDataType,
          programId,
          data?.dataDictionaryVersion || '',
          true,
          buildPath,
          draftId,
          browserHistory,
          setIsSubmitting,
        );
      } catch (err) {
        modalUtils.showError(err);
        setIsProcessing(false);
      }
    } else {
      setForceSubmitState(
        forceSubmitState === 'Force' ? 'ForceContinue' : 'Force',
      );
    }
  };

  return (
    <Flex
      alignItems='center'
      justifyContent='center'
      gap={3}
      className={classnames(className)}
      {...rest}
    >
      {isSaveDraftsAllowed && (
        <>
          <Button
            isDisabled={!isValidAsDraft || isProcessing}
            isLoading={loading}
            onClick={(_e) => {
              saveClickHandle(true, false);
            }}
            leftIcon={<FaSave />}
            colorScheme='gray'
            fontSize={'sm'}
            variant='outline'
          >
            Save and Exit
          </Button>
          <Button
            isDisabled={!isValidAsDraft || isProcessing}
            isLoading={loading}
            onClick={(_e) => {
              saveClickHandle(false, false);
            }}
            leftIcon={<FaSave />}
            colorScheme='gray'
            fontSize={'sm'}
            variant='outline'
          >
            Save
          </Button>
          <Button
            colorScheme='blue'
            ref={autoSaveRef}
            hidden
            onClick={(_e) => {
              saveClickHandle(false, true);
            }}
          ></Button>
        </>
      )}
      <Button
        isDisabled={isFirstStep}
        isLoading={loading}
        onClick={previousStep}
        leftIcon={<FaAngleDoubleLeft />}
        colorScheme='gray'
        fontSize={'sm'}
        variant='outline'
        h={8}
        boxShadow='lg'
        bg={'white'}
        border={'1px'}
        borderColor={'gray.200'}
        _hover={{
          background: 'white',
          color: 'blue.800',
          borderColor: 'blue.800',
        }}
      >
        Previous
      </Button>
      <Button
        isDisabled={isLastStep}
        isLoading={loading}
        onClick={nextStep}
        colorScheme='blue'
        rightIcon={<FaAngleDoubleRight />}
        fontSize={'sm'}
        h={8}
      >
        Next
      </Button>
      <Button
        isLoading={loading}
        colorScheme='blue'
        onClick={(_e) => {
          submitClickHandle();
        }}
        leftIcon={<FaSave />}
        fontSize={'sm'}
        h={8}
      >
        Submit
      </Button>
    </Flex>
  );
};

ProgramWizard.Header = ProgramWizardHeader;
ProgramWizard.Footer = ProgramWizardFooter;

export const ProgramWizardContainer = (
  props: OmitStrict<ProgramWizardProps, 'metadata'>,
) => {
  const history = useHistory();
  const location = useLocation();
  const { buildPath } = usePathContext();
  const params = qsParse((location.search || '').replace(/^\?/, ''));
  const isDraft = !!params?.draftId;
  const isClone = !isDraft && !!params?.from;
  const programWizardMetadata: ProgramWizardMetadata = {
    draftId: params?.draftId?.toString(),
    isDraft,
    isClone,
    from: params?.from?.toString(),
  };
  useSetPageTitle('Define Program');

  return (
    <SummaryCard
      containerProps={{ bg: 'gray.100', boxShadow: 'none' }}
      title=''
      secondaryTitle={
        <>
          <HStack>
            <Text
              fontSize='2xl'
              fontWeight='600'
              color={'black'}
              letterSpacing='wide'
            >
              Define Program
            </Text>
            <Text w='full'>
              <Prompt
                message={(location, _action) => {
                  if (location.state === 'EXIT') {
                    return true;
                  }
                  const userMessage =
                    'Are you sure you want to navigate away from your order request?';
                  return userMessage;
                }}
              ></Prompt>
              <Button
                colorScheme='gray'
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  history.replace(
                    buildPath(
                      `/dashboard?show=${params?.draftId ? 'drafts' : 'jobs'}`,
                    ),
                  );
                }}
                float='right'
                variant='solid'
                fontSize={'sm'}
                boxShadow='base'
                bg={'white'}
                border={'1px'}
                borderColor={'gray.600'}
                _hover={{
                  background: 'white',
                  color: 'blue.800',
                  borderColor: 'blue.800',
                }}
              >
                <FaBackward />
                &nbsp;&nbsp;
                {params?.draftId ? 'Back to Drafts' : 'Back to Dashboard'}
              </Button>
            </Text>
          </HStack>
          <Divider mt={4} />
        </>
      }
      details={
        <ProgramWizard
          {...props}
          metadata={programWizardMetadata}
        ></ProgramWizard>
      }
      detailsProps={{ fontSize: 'unset', fontWeight: 'unset' }}
    />
  );
};

// ProgramWizard.whyDidYouRender = true;
// ProgramWizardContainer.whyDidYouRender = true;
